import { useEffect, useState, useMemo } from "react";
import { getApiKeys, deleteApiKey } from "../api";

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt      = (n) => Number(n || 0).toLocaleString();
const fmtCost  = (n) => {
  const v = Number(n || 0);
  if (v === 0)   return "$0.00";
  if (v < 0.001) return `$${v.toFixed(6)}`;
  if (v < 1)     return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
};
const fmtTokens = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const fmtDateShort = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const RISK_COLORS = {
  low:      { bg: "#f0fff4", color: "#276749" },
  medium:   { bg: "#fffbeb", color: "#92400e" },
  high:     { bg: "#fff5f5", color: "#c53030" },
  critical: { bg: "#fff5f5", color: "#9b2c2c" },
};

const TIER_COLORS = {
  low:    { bg: "#f0fff4", color: "#276749" },
  medium: { bg: "#fffbeb", color: "#92400e" },
  high:   { bg: "#fff5f5", color: "#c53030" },
};

// ── Inline API Key cell ───────────────────────────────────────────────────────
function ApiKeyCell({ apiKey, compact = false }) {
  const [revealed, setRevealed] = useState(false);
  const [copied,   setCopied]   = useState(false);

  if (!apiKey) return <span style={{ color: "var(--muted)" }}>—</span>;

  const masked = apiKey.split("-").slice(0, 2).join("-") + "-••••••••••••";

  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <code style={{
        fontSize: compact ? 13 : 12,
        fontFamily: "monospace",
        background: "var(--bg)", padding: "2px 7px", borderRadius: 4,
        border: "1px solid var(--border)",
        color: "var(--text)",
        filter: revealed ? "none" : "blur(3.5px)",
        userSelect: revealed ? "text" : "none",
        transition: "filter 0.2s",
        whiteSpace: "nowrap",
        maxWidth: compact ? 340 : 200,
        overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {revealed ? apiKey : masked}
      </code>
      <button onClick={(e) => { e.stopPropagation(); setRevealed(v => !v); }}
        title={revealed ? "Hide key" : "Reveal key"}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14,
                 padding: "2px 4px", color: "var(--muted)", lineHeight: 1 }}>
        {revealed ? "🙈" : "👁"}
      </button>
      <button onClick={copy} title="Copy key"
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13,
                 padding: "2px 4px", color: copied ? "var(--success)" : "var(--muted)", lineHeight: 1 }}>
        {copied ? "✅" : "📋"}
      </button>
    </div>
  );
}

// ── Summary tile ──────────────────────────────────────────────────────────────
function Tile({ label, value, color, sub }) {
  return (
    <div className="stat-tile">
      <div className="label">{label}</div>
      <div className="value" style={color ? { color } : {}}>{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

// ── Info row helper ───────────────────────────────────────────────────────────
function InfoRow({ label, children }) {
  return (
    <div style={{ display: "flex", gap: 0, padding: "10px 0",
                  borderBottom: "1px solid var(--border)", alignItems: "flex-start" }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
                     letterSpacing: "0.05em", width: 148, flexShrink: 0, paddingTop: 1 }}>
        {label}
      </span>
      <span style={{ fontSize: 14, color: "var(--text)", flex: 1 }}>{children}</span>
    </div>
  );
}

// ── Key Detail view ───────────────────────────────────────────────────────────
function KeyDetail({ keyData: k, onBack, onViewRequest }) {
  const rc = RISK_COLORS[k.risk_level] || {};
  const tc = TIER_COLORS[k.model_tier?.toLowerCase()] || {};

  const costPerCall = k.total_calls > 0
    ? fmtCost(k.estimated_cost_usd / k.total_calls)
    : "—";

  const costPer1KTokens = k.total_tokens > 0
    ? fmtCost((k.estimated_cost_usd / k.total_tokens) * 1000)
    : "—";

  const latencyLabel = k.avg_latency_ms > 0
    ? k.avg_latency_ms >= 1000
        ? `${(k.avg_latency_ms / 1000).toFixed(2)}s`
        : `${Math.round(k.avg_latency_ms)}ms`
    : "—";

  return (
    <>
      {/* ── Page header with breadcrumb ── */}
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <button onClick={onBack}
            style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6,
                     padding: "4px 12px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                     color: "var(--muted)", display: "flex", alignItems: "center", gap: 5 }}>
            ← Back to Keys
          </button>
          <span style={{ color: "var(--border)", fontSize: 16 }}>›</span>
          <span style={{ fontSize: 13, color: "var(--muted)", fontFamily: "monospace" }}>
            {k.api_key ? k.api_key.split("-").slice(0, 2).join("-") + "-…" : "Key #" + k.id}
          </span>
        </div>
        <h1 style={{ fontSize: 24, marginBottom: 4 }}>🔑 Key Details</h1>
        <p style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 9999,
            background: k.is_active ? "var(--success-light)" : "var(--bg)",
            color:      k.is_active ? "var(--success)"       : "var(--muted)",
            border: `1px solid ${k.is_active ? "var(--success)" : "var(--border)"}`,
          }}>
            {k.is_active ? "● Active" : "○ Inactive"}
          </span>
          <span style={{ fontSize: 13, color: "var(--muted)" }}>
            Assigned {fmtDateShort(k.assigned_at)} by {k.assigned_by}
          </span>
        </p>
      </div>

      <div className="page-body">

        {/* ── Key cell ── */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-body" style={{ padding: "16px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
                          letterSpacing: "0.06em", marginBottom: 8 }}>API Key</div>
            <ApiKeyCell apiKey={k.api_key} compact />
          </div>
        </div>

        {/* ── Usage stat tiles ── */}
        <div className="stat-grid" style={{ marginBottom: 20 }}>
          <Tile label="Total API Calls"  value={fmt(k.total_calls)} />
          <Tile label="Total Tokens"     value={fmtTokens(k.total_tokens)} />
          <Tile label="Estimated Cost"   value={fmtCost(k.estimated_cost_usd)} color="var(--warning)"
                sub="lifetime spend for this key" />
          <Tile label="Avg Latency"      value={latencyLabel}
                sub={k.avg_latency_ms > 0 ? "rolling average" : "no calls yet"} />
          <Tile label="Cost / Call"      value={costPerCall}
                sub={k.total_calls > 0 ? "per API call" : "no calls yet"} />
          <Tile label="Cost / 1K Tokens" value={costPer1KTokens}
                sub={k.total_tokens > 0 ? "effective rate" : "no calls yet"} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

          {/* ── Request info ── */}
          <div className="card">
            <div className="card-header">Request</div>
            <div className="card-body" style={{ padding: "4px 20px 12px" }}>
              <InfoRow label="Title">{k.request_title}</InfoRow>
              <InfoRow label="Request ID">
                <span style={{ fontFamily: "monospace", fontSize: 13 }}>#{k.request_id}</span>
              </InfoRow>
              <InfoRow label="Domain">
                <span style={{ textTransform: "capitalize" }}>{k.domain}</span>
              </InfoRow>
              <InfoRow label="Risk Level">
                <span style={{
                  display: "inline-block", fontSize: 12, fontWeight: 700,
                  padding: "2px 8px", borderRadius: 4,
                  background: rc.bg, color: rc.color,
                }}>
                  {k.risk_level}
                </span>
                {k.risk_score != null && (
                  <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: 8 }}>
                    score {k.risk_score}/10
                  </span>
                )}
              </InfoRow>
              <div style={{ marginTop: 14 }}>
                <button className="btn btn-outline btn-sm"
                  onClick={() => onViewRequest?.(k.request_id)}>
                  View Full Request →
                </button>
              </div>
            </div>
          </div>

          {/* ── Requester info ── */}
          <div className="card">
            <div className="card-header">Requester</div>
            <div className="card-body" style={{ padding: "4px 20px 12px" }}>
              <InfoRow label="Name">{k.requester}</InfoRow>
              <InfoRow label="Email">
                <a href={`mailto:${k.requester_email}`}
                   style={{ color: "var(--primary)", textDecoration: "none" }}>
                  {k.requester_email}
                </a>
              </InfoRow>
              <InfoRow label="Team">{k.team_name || "—"}</InfoRow>
              <InfoRow label="Manager">
                {k.manager_email
                  ? <a href={`mailto:${k.manager_email}`}
                       style={{ color: "var(--muted)", textDecoration: "none", fontSize: 13 }}>
                      {k.manager_email}
                    </a>
                  : "—"}
              </InfoRow>
            </div>
          </div>

          {/* ── Model info ── */}
          <div className="card">
            <div className="card-header">Model</div>
            <div className="card-body" style={{ padding: "4px 20px 12px" }}>
              <InfoRow label="Model">
                <strong>{k.model_name}</strong>
              </InfoRow>
              <InfoRow label="Provider">{k.model_provider}</InfoRow>
              <InfoRow label="Tier">
                <span style={{
                  display: "inline-block", fontSize: 12, fontWeight: 700,
                  padding: "2px 8px", borderRadius: 4,
                  background: tc.bg || "var(--bg)", color: tc.color || "var(--muted)",
                }}>
                  {k.model_tier || "—"}
                </span>
              </InfoRow>
              <InfoRow label="List Price">
                ${k.cost_per_1m_tokens?.toFixed(2) ?? "—"} / 1M tokens
              </InfoRow>
            </div>
          </div>

          {/* ── Assignment info ── */}
          <div className="card">
            <div className="card-header">Assignment</div>
            <div className="card-body" style={{ padding: "4px 20px 12px" }}>
              <InfoRow label="Assigned At">{fmtDate(k.assigned_at)}</InfoRow>
              <InfoRow label="Assigned By">{k.assigned_by}</InfoRow>
              <InfoRow label="Last Used">
                {k.last_used_at ? fmtDate(k.last_used_at) : (
                  <span style={{ color: "var(--muted)", fontSize: 13 }}>Never used</span>
                )}
              </InfoRow>
              <InfoRow label="Reason">
                {k.assignment_reason
                  ? <span style={{ fontSize: 13 }}>{k.assignment_reason}</span>
                  : <span style={{ color: "var(--muted)", fontSize: 13 }}>—</span>}
              </InfoRow>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ApiKeys({ onViewRequest }) {
  const [keys,        setKeys]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [search,      setSearch]      = useState("");
  const [activeOnly,  setActiveOnly]  = useState(false);
  const [sortBy,      setSortBy]      = useState("assigned_at");
  const [sortDir,     setSortDir]     = useState("desc");
  const [selectedKey, setSelectedKey] = useState(null);   // null = list, object = detail
  const [deletingId,  setDeletingId]  = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    getApiKeys()
      .then(setKeys)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const handleDelete = async (k, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete API key for "${k.request_title}"?\n\nThis permanently removes the key and revokes access. This cannot be undone.`))
      return;
    setDeletingId(k.id);
    try {
      await deleteApiKey(k.id);
      setKeys((prev) => prev.filter((x) => x.id !== k.id));
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Key detail view ──
  if (selectedKey) {
    return (
      <KeyDetail
        keyData={selectedKey}
        onBack={() => setSelectedKey(null)}
        onViewRequest={(id) => {
          setSelectedKey(null);          // return to list first
          onViewRequest?.(id);           // then navigate to request
        }}
      />
    );
  }

  // Client-side filter + sort
  const visible = useMemo(() => {
    let rows = keys;
    if (activeOnly) rows = rows.filter((k) => k.is_active);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      rows = rows.filter((k) =>
        k.requester.toLowerCase().includes(s) ||
        k.requester_email.toLowerCase().includes(s) ||
        k.request_title.toLowerCase().includes(s) ||
        String(k.request_id).includes(s) ||
        k.model_name.toLowerCase().includes(s) ||
        (k.team_name || "").toLowerCase().includes(s)
      );
    }
    rows = [...rows].sort((a, b) => {
      let va, vb;
      if (sortBy === "assigned_at") { va = a.assigned_at;         vb = b.assigned_at; }
      else if (sortBy === "cost")   { va = a.estimated_cost_usd;  vb = b.estimated_cost_usd; }
      else if (sortBy === "calls")  { va = a.total_calls;         vb = b.total_calls; }
      else                           { va = a.total_tokens;        vb = b.total_tokens; }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [keys, activeOnly, search, sortBy, sortDir]);

  // Summary stats over ALL keys
  const total       = keys.length;
  const activeCount = keys.filter((k) => k.is_active).length;
  const totalCalls  = keys.reduce((s, k) => s + (k.total_calls || 0), 0);
  const totalTokens = keys.reduce((s, k) => s + (k.total_tokens || 0), 0);
  const totalCost   = keys.reduce((s, k) => s + (k.estimated_cost_usd || 0), 0);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };
  const sortIcon = (col) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <>
      <div className="page-header">
        <h1>🔑 API Keys</h1>
        <p>All issued model API keys — click any row for detailed stats and usage breakdown.</p>
      </div>

      <div className="page-body">
        {error && <div className="error-msg" style={{ marginBottom: 16 }}>⚠ {error}</div>}

        {/* ── Summary tiles ── */}
        <div className="stat-grid" style={{ marginBottom: 24 }}>
          <Tile label="Total Keys"      value={fmt(total)} />
          <Tile label="Active Keys"     value={fmt(activeCount)} color="var(--success)" />
          <Tile label="Total API Calls" value={fmt(totalCalls)} />
          <Tile label="Total Tokens"    value={fmtTokens(totalTokens)} />
          <Tile label="Total Cost"      value={fmtCost(totalCost)} color="var(--warning)"
                sub="across all time" />
        </div>

        {/* ── Filters ── */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
          <input
            type="search"
            placeholder="🔍  Search by requester, request, model, team…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: "1 1 260px", maxWidth: 400 }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                          fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              style={{ width: "auto", accentColor: "var(--success)" }} />
            Active keys only
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
            <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Sort:</span>
            {[["assigned_at","Date"],["cost","Cost"],["calls","Calls"],["tokens","Tokens"]].map(([col, lbl]) => (
              <button key={col} onClick={() => toggleSort(col)}
                style={{
                  padding: "4px 10px", borderRadius: 6, border: "1px solid",
                  fontSize: 12, fontWeight: sortBy === col ? 700 : 500, cursor: "pointer",
                  background:  sortBy === col ? "var(--primary)"  : "var(--surface)",
                  color:       sortBy === col ? "#fff"             : "var(--muted)",
                  borderColor: sortBy === col ? "var(--primary)"   : "var(--border)",
                }}>
                {lbl}{sortIcon(col)}
              </button>
            ))}
          </div>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? "…" : "↺ Refresh"}
          </button>
        </div>

        {/* ── Result count ── */}
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
          Showing {visible.length} of {total} key{total !== 1 ? "s" : ""}
          {(search || activeOnly) && " (filtered)"}
          <span style={{ marginLeft: 8, opacity: 0.7 }}>· Click a row to view details</span>
        </div>

        {/* ── Table ── */}
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>API Key</th>
                  <th>Request</th>
                  <th>Requester</th>
                  <th>Team</th>
                  <th>Model</th>
                  <th style={{ cursor: "pointer" }} onClick={() => toggleSort("assigned_at")}>
                    Assigned{sortIcon("assigned_at")}
                  </th>
                  <th>Last Used</th>
                  <th style={{ cursor: "pointer", textAlign: "right" }} onClick={() => toggleSort("calls")}>
                    Calls{sortIcon("calls")}
                  </th>
                  <th style={{ cursor: "pointer", textAlign: "right" }} onClick={() => toggleSort("tokens")}>
                    Tokens{sortIcon("tokens")}
                  </th>
                  <th style={{ cursor: "pointer", textAlign: "right" }} onClick={() => toggleSort("cost")}>
                    Cost{sortIcon("cost")}
                  </th>
                  <th>Status</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={12} className="empty">Loading…</td></tr>
                )}
                {!loading && visible.length === 0 && (
                  <tr><td colSpan={12} className="empty">No API keys found.</td></tr>
                )}
                {visible.map((k) => {
                  const rc = RISK_COLORS[k.risk_level] || {};
                  return (
                    <tr key={k.id}
                      onClick={() => setSelectedKey(k)}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "var(--primary-light, #eef0ff)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "";
                      }}
                    >
                      {/* API Key */}
                      <td style={{ minWidth: 240 }}>
                        <ApiKeyCell apiKey={k.api_key} />
                      </td>

                      {/* Request */}
                      <td style={{ minWidth: 180 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{k.request_title}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>#{k.request_id}</span>
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 4,
                            background: rc.bg, color: rc.color,
                          }}>{k.risk_level}</span>
                          <span style={{ fontSize: 11, color: "var(--muted)", textTransform: "capitalize" }}>
                            {k.domain}
                          </span>
                        </div>
                      </td>

                      {/* Requester */}
                      <td style={{ minWidth: 160 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{k.requester}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                          <a href={`mailto:${k.requester_email}`}
                             onClick={(e) => e.stopPropagation()}
                             style={{ color: "var(--primary)", textDecoration: "none" }}>
                            {k.requester_email}
                          </a>
                        </div>
                      </td>

                      {/* Team */}
                      <td className="text-muted">{k.team_name || "—"}</td>

                      {/* Model */}
                      <td style={{ minWidth: 140 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{k.model_name}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>{k.model_provider}</div>
                      </td>

                      {/* Assigned */}
                      <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                        <div>{fmtDateShort(k.assigned_at)}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>by {k.assigned_by}</div>
                      </td>

                      {/* Last used */}
                      <td style={{ whiteSpace: "nowrap", fontSize: 13, color: "var(--muted)" }}>
                        {k.last_used_at ? fmtDateShort(k.last_used_at) : "Never"}
                      </td>

                      {/* Calls */}
                      <td style={{ fontWeight: 600, textAlign: "right" }}>{fmt(k.total_calls)}</td>

                      {/* Tokens */}
                      <td style={{ textAlign: "right" }}>{fmtTokens(k.total_tokens)}</td>

                      {/* Cost */}
                      <td style={{ fontWeight: 700, textAlign: "right",
                                   color: k.estimated_cost_usd > 0 ? "var(--warning)" : "var(--muted)" }}>
                        {fmtCost(k.estimated_cost_usd)}
                      </td>

                      {/* Status */}
                      <td>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          fontSize: 12, fontWeight: 600, padding: "3px 8px", borderRadius: 9999,
                          background: k.is_active ? "var(--success-light)" : "var(--bg)",
                          color:      k.is_active ? "var(--success)"       : "var(--muted)",
                          border: `1px solid ${k.is_active ? "var(--success)" : "var(--border)"}`,
                        }}>
                          {k.is_active ? "● Active" : "○ Inactive"}
                        </span>
                      </td>

                      {/* Navigate to request */}
                      <td>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={(e) => { e.stopPropagation(); onViewRequest?.(k.request_id); }}
                          title={`Open request #${k.request_id}`}
                        >
                          View →
                        </button>
                      </td>

                      {/* Delete key */}
                      <td>
                        <button
                          className="btn btn-sm"
                          onClick={(e) => handleDelete(k, e)}
                          disabled={deletingId === k.id}
                          title="Permanently delete this API key"
                          style={{
                            background: "var(--danger-light)",
                            color: "var(--danger)",
                            border: "1px solid var(--danger)",
                            opacity: deletingId === k.id ? 0.5 : 1,
                          }}
                        >
                          {deletingId === k.id ? "…" : "Delete"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Usage breakdown by model ── */}
        {keys.length > 0 && (
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-header">Usage by Model</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Model</th><th>Provider</th><th>Keys</th><th>Active</th>
                    <th>Calls</th><th>Tokens</th><th>Cost</th><th>$/1M tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(
                    keys.reduce((acc, k) => {
                      const m = k.model_name;
                      if (!acc[m]) acc[m] = {
                        name: m, provider: k.model_provider,
                        keys: 0, active: 0, calls: 0, tokens: 0, cost: 0,
                        rate: k.cost_per_1m_tokens,
                      };
                      acc[m].keys   += 1;
                      acc[m].active += k.is_active ? 1 : 0;
                      acc[m].calls  += k.total_calls  || 0;
                      acc[m].tokens += k.total_tokens || 0;
                      acc[m].cost   += k.estimated_cost_usd || 0;
                      return acc;
                    }, {})
                  ).sort((a, b) => b.cost - a.cost).map((m) => (
                    <tr key={m.name}>
                      <td><strong>{m.name}</strong></td>
                      <td className="text-muted">{m.provider}</td>
                      <td>{m.keys}</td>
                      <td style={{ color: "var(--success)", fontWeight: 600 }}>{m.active}</td>
                      <td>{fmt(m.calls)}</td>
                      <td>{fmtTokens(m.tokens)}</td>
                      <td style={{ fontWeight: 700, color: m.cost > 0 ? "var(--warning)" : "var(--muted)" }}>
                        {fmtCost(m.cost)}
                      </td>
                      <td className="text-muted">${m.rate?.toFixed(2) ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
